/**
 * RÉFÉRENTIELS → TAUX DE TVA — les taux que le magasin propose, le taux par défaut de chaque famille, et les modèles qui ne
 * suivent pas le taux de leur famille (maquette, onglet `tva`).
 *
 * ⚠️ LES TAUX NE SE CRÉENT PAS : ce sont ceux de la loi. Le magasin choisit seulement ceux que ses menus proposent
 *    (`vat.offered_rates`) ; un taux qu'un modèle en service, une famille ou le défaut du magasin porte ne se retire pas — le
 *    serveur le refuse (`vatRatesGuard`), et l'écran le dit sur l'interrupteur au lieu d'offrir un geste qui échoue.
 * ⚠️ LE TAUX D'UNE FAMILLE S'APPLIQUE AUX CRÉATIONS, JAMAIS AUX ARTICLES EXISTANTS ; « Celui du magasin » le rend au taux par
 *    défaut du magasin (Paramètres). Le dessin n'a pas cette option : chaque famille y porte un taux déduit de ses articles.
 * ⚠️ UN ÉCART N'EST PAS UNE FAUTE : « Aligner » est proposé, jamais fait d'office ; le serveur relit le taux de la famille au
 *    moment d'écrire.
 * ⚠️ EN FRANCHISE EN BASE, l'onglet le dit d'abord : la caisse vend tout à 0 %, quel que soit le taux des fiches.
 */
import { useCallback, useEffect, useState } from 'react';
import { confirm, showToast, Tooltip, Trans } from '@orgazone/ui-web';
import { formatOfferedVatRates } from '@orgazone/sync-contract';
import { formatRateBp } from '@orgazone/core-vat';
import { api, type ReferentialVat, type ReferentialVatRate } from '../api';
import { key, t, type Translatable } from '../i18n';
import { vatRateOptions } from '../vat-rates';
import { FacetError, FacetReadFailure, reasonOf } from './referential-facets-shared';

/** Ce que l'écran dit de chaque taux de la loi : son nom et l'usage qu'on en fait (le dessin). */
const RATE_TEXT: Readonly<Record<number, readonly [Translatable, Translatable]>> = {
  2000: [key('Taux normal'), key('Cas général — habillement, accessoires')],
  1000: [key('Taux intermédiaire'), key('Restauration sur place, travaux')],
  550: [key('Taux réduit'), key('Produits alimentaires, livres')],
  210: [key('Taux particulier'), key('Presse, médicaments remboursables')],
  0: [key('Exonéré'), key('Exportations, livraisons intracommunautaires')],
};
const OLD_RATE_TEXT: readonly [Translatable, Translatable] = [key('Taux ancien'), key('Porté par des articles repris — il ne se propose plus')];

const CARD = { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' } as const;
const HEAD = { display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' } as const;
const HEAD_TITLE = { flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' } as const;
const HEAD_NOTE = { fontSize: 'var(--t-body)', color: 'var(--ink-500)', flexShrink: 0 } as const;
const FOOT = { padding: '14px 18px', background: 'var(--ink-50)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 } as const;
const strong = (x: string) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>;

/** Pourquoi un taux proposé ne se retire pas — `null` quand il se retire. La règle du serveur, dite avant le geste. */
export function lockedReason(rate: ReferentialVatRate, storeDefaultBp: number | null): string | null {
  if (rate.models > 0) return t('Porté par {n} modèle en service — retrait impossible', { n: rate.models });
  if (rate.families > 0) return t('Taux par défaut de {n} famille — retrait impossible', { n: rate.families });
  if (rate.rateBp === storeDefaultBp) return t('Taux par défaut du magasin — retrait impossible');
  return null;
}

/** « 3 modèles », « aucun modèle » — la colonne de compte des deux premières cartes. */
const modelsText = (n: number): string => (n === 0 ? t('aucun modèle') : t('{n} modèle', { n }));

export function VatRatesTab({ onWritten }: {
  /** Ce qu'il faut relire après une écriture : la colonne qui compte la liste. */
  onWritten: () => void;
}) {
  const [data, setData] = useState<ReferentialVat | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(() => {
    api.referentielVat()
      .then((r) => { setData(r); setFailure(null); })
      // ⚠️ L'ÉCHEC SE DIT : des listes vides se liraient « aucun taux porté », et l'on retirerait un taux en service.
      .catch((e: unknown) => { setData(null); setFailure(reasonOf(e)); });
  }, []);
  useEffect(read, [read]);

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!data) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;

  /** Une écriture, puis la relecture — le refus du serveur s'affiche tel quel. */
  const write = async (gesture: () => Promise<unknown>, done: string) => {
    setBusy(true); setError(null);
    try {
      await gesture();
      showToast(done);
      onWritten();
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    read();
  };

  const offeredNow = data.offered.ratesBp ?? data.rates.filter((r) => r.legal).map((r) => r.rateBp);
  const toggle = (rate: ReferentialVatRate) => {
    if (busy || !rate.legal) return;
    if (rate.offered && lockedReason(rate, data.storeDefault.rateBp)) return;
    const next = rate.offered ? offeredNow.filter((bp) => bp !== rate.rateBp) : [...offeredNow, rate.rateBp];
    const label = formatRateBp(rate.rateBp);
    void write(
      () => api.writeMasterData('setting', [{ name: 'vat.offered_rates', value: formatOfferedVatRates(next) }], `vat:offered:${formatOfferedVatRates(next)}`),
      rate.offered ? t('{v} n’est plus proposé.', { v: label }) : t('{v} est proposé.', { v: label }),
    );
  };

  const setFamilyRate = (code: string, label: string, value: string) => {
    const bp = value === '' ? null : Number(value);
    void write(
      () => api.writeMasterData('family', [{ code, vatRateBp: bp }], `vat:family:${code}:${value || 'store'}`),
      bp === null ? t('« {v} » suit le taux du magasin.', { v: label }) : t('« {v} » : {r} par défaut.', { v: label, r: formatRateBp(bp) }),
    );
  };

  const alignOne = (modelKey: string, name: string) => {
    void write(() => api.alignVatRates([modelKey], `vat:align:${modelKey}`), t('« {v} » suit le taux de sa famille.', { v: name }));
  };
  const alignAll = async () => {
    const ok = await confirm({
      title: t('Aligner {n} modèle ?', { n: data.off.models }),
      message: t('{n} déclinaison passe au taux par défaut de sa famille. Les ventes passées gardent leur taux.', { n: data.off.variants }),
      confirmLabel: t('Tout aligner'), cancelLabel: t('Annuler'),
    });
    if (!ok) return;
    void write(() => api.alignVatRates(null, 'vat:align:all'), t('{n} modèle aligné sur le taux de sa famille.', { n: data.off.models }));
  };

  const storeLabel = data.storeDefault.rateBp === null ? '—' : formatRateBp(data.storeDefault.rateBp);
  const offCount = data.off.models;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {data.franchise && (
        <p role="status" style={{ margin: 0, padding: '12px 16px', border: '1px solid var(--amber)', borderRadius: 'var(--r-md)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-body)', fontWeight: 600, lineHeight: 1.5 }}>
          {t('Le magasin est en franchise en base de TVA (art. 293 B du CGI) : la caisse vend tout à 0 %, quel que soit le taux des fiches. Les taux ci-dessous servent le jour où le magasin sort de la franchise.')}
        </p>
      )}
      {data.storeDefault.rateBp === null && (
        <p role="alert" style={{ margin: 0, padding: '12px 16px', border: '1px solid var(--rose)', borderRadius: 'var(--r-md)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)', fontWeight: 600, lineHeight: 1.5 }}>
          {t('Le taux par défaut du magasin (« {v} ») n’est pas un taux : aucun article ne peut être créé sans taux tant qu’il n’est pas corrigé dans Paramètres.', { v: data.storeDefault.stored ?? '' })}
        </p>
      )}
      <FacetError message={error} />

      <div style={CARD}>
        <div style={HEAD}>
          <span style={HEAD_TITLE}>{t('Taux en vigueur')}</span>
          <span style={HEAD_NOTE}>{t('{n} utilisé dans ce magasin', { n: data.rates.filter((r) => r.offered).length })}</span>
        </div>
        {data.rates.map((r) => {
          const [name, usage] = r.legal ? RATE_TEXT[r.rateBp] ?? OLD_RATE_TEXT : OLD_RATE_TEXT;
          const locked = r.offered ? lockedReason(r, data.storeDefault.rateBp) : null;
          return (
            <div key={r.rateBp} style={{ display: 'grid', gridTemplateColumns: '88px minmax(0,1fr) 130px 68px', gap: 12, padding: '0 18px', minHeight: 52, alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-h4)', fontWeight: 700, color: r.offered ? 'var(--ink-900)' : 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>{formatRateBp(r.rateBp)}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{t(name)}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1 }}>{t(usage)}</span>
              </span>
              <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{modelsText(r.models)}</span>
              <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {r.legal && (
                  <Tooltip content={locked ?? t('Proposer ou retirer ce taux')}>
                    <button type="button" role="switch" aria-checked={r.offered} aria-label={t('Proposer le taux {v}', { v: formatRateBp(r.rateBp) })}
                      aria-disabled={busy || locked !== null || undefined}
                      onClick={() => { if (!busy && locked === null) toggle(r); }}
                      style={{ border: 'none', background: 'transparent', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: busy || locked ? 'default' : 'pointer' }}>
                      <span style={{
                        width: 38, height: 22, borderRadius: 'var(--r-pill)', background: r.offered ? 'var(--teal-500)' : 'var(--ink-200)', display: 'flex', alignItems: 'center',
                        justifyContent: r.offered ? 'flex-end' : 'flex-start', padding: '0 3px', flexShrink: 0, boxSizing: 'border-box', opacity: locked ? 0.5 : 1,
                      }}>
                        <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} />
                      </span>
                    </button>
                  </Tooltip>
                )}
              </span>
            </div>
          );
        })}
        <div style={FOOT}>
          <Trans k='<1>Les taux ne se créent pas</1> — ce sont ceux du code général des impôts. On choisit seulement lesquels servent ici, pour raccourcir les menus. Un taux <2>porté par au moins un article ne peut pas être retiré</2>.'
            parts={[strong, strong]} />
        </div>
      </div>

      <div style={CARD}>
        <div style={HEAD}>
          <span style={HEAD_TITLE}>{t('Taux par défaut, par famille')}</span>
          <span style={HEAD_NOTE}>{t('Appliqué aux créations, jamais aux articles existants')}</span>
        </div>
        {data.families.length === 0 && (
          <p style={{ margin: 0, padding: '16px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune famille : les articles créés prennent le taux du magasin ({v}).', { v: storeLabel })}</p>
        )}
        {data.families.map((f) => (
          <div key={f.code} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 108px 150px', gap: 12, padding: '9px 18px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <span aria-hidden style={{ width: 9, height: 9, borderRadius: 3, background: f.color ?? 'var(--ink-300)', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: f.isActive ? 'var(--ink-900)' : 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
            </span>
            <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{modelsText(f.models)}</span>
            <select value={f.vatRateBp === null ? '' : String(f.vatRateBp)} disabled={busy} aria-label={t('Taux par défaut de « {v} »', { v: f.label })}
              onChange={(e) => setFamilyRate(f.code, f.label, e.target.value)}
              style={{ height: 38, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none', width: '100%' }}>
              <option value="">{t('Celui du magasin ({v})', { v: storeLabel })}</option>
              {vatRateOptions(data.offered.ratesBp, f.vatRateBp).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div style={{ ...CARD, border: `1px solid ${offCount > 0 ? 'var(--amber)' : 'var(--ink-200)'}` }}>
        <div style={{ ...HEAD, background: offCount > 0 ? 'var(--amber-bg)' : 'var(--paper)' }}>
          <span style={{ ...HEAD_TITLE, minWidth: 170 }}>{t('Articles hors taux par défaut')}</span>
          {offCount > 0 && (
            <button type="button" disabled={busy} onClick={() => void alignAll()}
              style={{ height: 36, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-lead)', fontWeight: 600, flexShrink: 0, cursor: busy ? 'default' : 'pointer' }}>
              {t('Tout aligner')}</button>
          )}
        </div>
        {data.off.rows.map((o) => (
          <div key={o.modelKey} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 96px 96px 120px', gap: 12, padding: '10px 18px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</span>
              <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 2 }}>
                {[o.articleCode, o.family].filter(Boolean).join(' · ')}</span>
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--amber-fg)', fontVariantNumeric: 'tabular-nums' }}>
              {o.ratesBp.map((bp) => formatRateBp(bp)).join(' · ')}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
              {t('→ {v}', { v: formatRateBp(o.defaultBp) })}</span>
            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" disabled={busy} onClick={() => alignOne(o.modelKey, o.name)} aria-label={t('Aligner « {v} »', { v: o.name })}
                style={{ height: 36, padding: '0 13px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer' }}>
                {t('Aligner')}</button>
            </span>
          </div>
        ))}
        {offCount > data.off.rows.length && (
          <p style={{ margin: 0, padding: '10px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-600)', borderBottom: '1px solid var(--ink-100)' }}>
            {t('Et {n} autre modèle — « Tout aligner » les traite aussi.', { n: offCount - data.off.rows.length })}</p>
        )}
        {offCount === 0 && (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Tous les articles suivent le taux de leur famille')}</div>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Un écart réapparaîtra ici dès qu’un taux sera saisi à la main sur une fiche.')}</div>
          </div>
        )}
        <div style={{ ...FOOT, borderTop: '1px solid var(--ink-200)' }}>
          <Trans k='<1>Un écart n’est pas une faute</1> — un article peut légitimement relever d’un autre taux. Il est seulement <2>signalé</2>, jamais corrigé d’office. Et changer un taux <3>ne réécrit aucun ticket</3> : les ventes passées gardent le leur, l’effet commence à la vente suivante.'
            parts={[strong, strong, strong]} />
        </div>
      </div>
    </div>
  );
}
